import React from 'react';
import classnames from '../utils/classnames.tsx';

export const SettingsCard = ({
    title,
    description,
    className,
    descriptionClassName,
    children,
    controls,
    footer,
    ...props
}: Omit<React.ComponentPropsWithRef<'section'>, 'title'> & {
    title?: React.ReactNode;
    description?: React.ReactNode;
    children: React.ReactNode;
    controls?: React.ReactNode;
    // the card's action strip (Automation Hub settings cards): holds the primary button, e.g. "Save changes"
    footer?: React.ReactNode;
    descriptionClassName?: string;
}) => {
    return (
        <section
            {...props}
            className={classnames(
                'w-full max-w-[48rem] border border-solid border-line-weak bg-surface-weak flex flex-col rounded-lg p-8 gap-6',
                className
            )}
        >
            {(title || controls || description) && (
                // the description belongs to the title, so it sits close under it, not a card gap away; it stays
                // narrower than the card, so it doesn't run under the controls next to the title
                <div className="flex flex-col gap-1.5">
                    {(title || controls) && (
                        <div className="flex items-center justify-between gap-4">
                            {title && <SettingsCardTitle>{title}</SettingsCardTitle>}
                            {controls && <div className="flex-shrink-0">{controls}</div>}
                        </div>
                    )}
                    {description && (
                        <SettingsCardDescription className={classnames('max-w-2xl', descriptionClassName)}>
                            {description}
                        </SettingsCardDescription>
                    )}
                </div>
            )}
            {children}
            {footer && <SettingsCardFooter>{footer}</SettingsCardFooter>}
        </section>
    );
};

const SettingsFormCard = ({
    formClassName,
    children,
    onSubmit,
    footer,
    ...props
}: React.ComponentPropsWithRef<typeof SettingsCard> & {
    onSubmit?: (formData: any) => void;
    formClassName?: string;
}) => {
    return (
        <SettingsCard {...props}>
            {/* the footer sits inside the form so its submit button belongs to it */}
            <form className={classnames('flex flex-col gap-6', formClassName)} onSubmit={onSubmit}>
                {children}
                {footer && <SettingsCardFooter>{footer}</SettingsCardFooter>}
            </form>
        </SettingsCard>
    );
};

const SettingsCardFooter = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div
        className={classnames(
            '-mx-8 -mb-8 mt-2 flex items-center justify-end gap-2 rounded-b-lg border-0 border-t border-solid border-line-weak bg-surface-weak px-8 py-4',
            className
        )}
        {...props}
    />
);

const SettingsCardTitle = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div className={classnames('text-base font-semibold text-content-strong', className)} {...props} />
);

const SettingsCardDescription = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div className={classnames('text-sm text-content-medium', className)} {...props} />
);

export default Object.assign(SettingsFormCard, { Title: SettingsCardTitle, Footer: SettingsCardFooter });
